import { getPathname } from '@/i18n/navigation';
import { routing, type Locale } from '@/i18n/routing';

/**
 * Domain roles:
 * - NEXT_PUBLIC_DOMAIN (yaponamama.uz) — public host for SEO (canonical, hreflang, OG, JSON-LD)
 * - NEXT_PUBLIC_DELEVER_DOMAIN (yaponamama.delever.uz) — panel / website-settings API domain
 * - test-yaponamama.delever.uz — staging site under test
 */
const PUBLIC_SEO_ORIGIN = `https://${process.env.NEXT_PUBLIC_DOMAIN!}`;

export function getLocalePath(locale: Locale | string, href: string): string {
  return getPathname({ locale: locale as Locale, href });
}

export function getAbsoluteLocaleUrl(
  baseUrl: string,
  locale: Locale | string,
  href: string,
): string {
  return `${baseUrl}${getPathname({ locale: locale as Locale, href })}`;
}

/** Map panel/test hosts → public production origin for SEO tags. */
export function resolvePublicSeoBaseUrl(baseUrl: string): string {
  if (
    baseUrl.includes('yaponamama.delever.uz') ||
    baseUrl.includes('test-yaponamama.delever.uz')
  ) {
    return PUBLIC_SEO_ORIGIN;
  }
  return baseUrl;
}

/** Absolute SEO origin for OpenGraph, JSON-LD, breadcrumbs. */
export function getPublicSeoOrigin(): string {
  return PUBLIC_SEO_ORIGIN;
}

/** Locale-aware absolute URL on the public SEO host (canonical, OG url, JSON-LD). */
export function getCanonicalLocaleUrl(
  baseUrl: string,
  locale: Locale | string,
  href: string,
): string {
  return getAbsoluteLocaleUrl(resolvePublicSeoBaseUrl(baseUrl), locale, href);
}

export function buildLanguageAlternates(baseUrl: string, href: string) {
  const seoBaseUrl = resolvePublicSeoBaseUrl(baseUrl);
  const languages: Record<string, string> = {};
  for (const loc of routing.locales) {
    languages[loc] = getAbsoluteLocaleUrl(seoBaseUrl, loc, href);
  }
  languages['x-default'] = getAbsoluteLocaleUrl(
    seoBaseUrl,
    routing.defaultLocale,
    href,
  );
  return languages;
}
